/** Putting the game down and picking it up again.
 *
 * The whole format lives in `@game/core`; this file is the *host* half - the
 * two places a browser can keep a file, and nothing else. That split is I11
 * mechanised rather than promised: the shell holds the camera, the selection,
 * the open panel and the animation clock, and none of them can reach a save
 * because the only thing handed to the serializer is `session.getState()`,
 * which is a `GameState` and cannot be anything else.
 *
 * Everything a save *browser* wants to show - when it was written, which day it
 * is on, how large it is - lives in a separate index in local storage, not in
 * the file. A metadata block inside the file is exactly the doorway a camera
 * position walks through six months later.
 */

import type { ContentRegistry, GameState, PersistError } from "@game/core";
import { loadGame, parseSave, persistErrorText, saveGame } from "@game/core";
import type { GameOptions } from "./newgame.js";

/** One key per slot, plus one index. Versioned, so a format change can clear
 *  its own leftovers instead of failing to parse them forever. */
const PREFIX = "realm.save.v1:";
const INDEX_KEY = "realm.save.v1.index";
/** The setup the last game was started or resumed on. See `rememberOptions`. */
const OPTIONS_KEY = "realm.options.v1";
export const AUTOSAVE_SLOT = "autosave";
/** The slot F5 writes and F9 reads. Separate from the autosave on purpose: an
 *  autosave a turn later is exactly the thing a quicksave is insurance against. */
export const QUICKSAVE_SLOT = "quicksave";

export interface SlotInfo {
  readonly slot: string;
  readonly day: number;
  readonly week: number;
  readonly month: number;
  /** Milliseconds since the epoch, from the host's clock. The kernel has no
   *  clock (I2); this is a file timestamp, not a game fact. */
  readonly savedAt: number;
  readonly bytes: number;
  /**
   * The setup this game was started on, kept beside the file rather than in it.
   *
   * Difficulty is not a game fact - it tunes the computer's planner and hands
   * the human his opening purse, and neither is recoverable from a `GameState`
   * on day forty. But a player who set Impossible and came back tomorrow was
   * being given a Normal opponent, because the only thing that remembered the
   * level was a field on a model that died with the tab. Putting it in the
   * *index* rather than in the save keeps the file exactly what it was - a
   * `GameState` and nothing else, which is what makes I11 hold by construction
   * - while giving the host somewhere to remember a host concern.
   *
   * A save that came from a file has no index row, so it resumes on the last
   * remembered setup instead; see `rememberedOptions`.
   */
  readonly options?: GameOptions;
}

export type SaveOutcome =
  | { readonly ok: true; readonly info: SlotInfo }
  | { readonly ok: false; readonly message: string };

export type LoadOutcome =
  | {
    readonly ok: true;
    readonly state: GameState;
    readonly migrated: readonly string[];
    /** From the slot's index row, when the save came from a slot. */
    readonly options?: GameOptions;
  }
  | { readonly ok: false; readonly message: string };

// ------------------------------------------------------------------ storage

/** Local storage is absent in a private window, in some embeds, and whenever a
 *  browser is set to block site data. None of those is an exception. */
function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    // Presence is not access: Safari throws on read in a locked-down context.
    s.getItem(INDEX_KEY);
    return s;
  } catch {
    return null;
  }
}

function readIndex(): readonly SlotInfo[] {
  const s = storage();
  if (s === null) return [];
  try {
    const raw = s.getItem(INDEX_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is SlotInfo =>
      e !== null && typeof e === "object" && typeof (e as SlotInfo).slot === "string");
  } catch {
    return [];
  }
}

function writeIndex(rows: readonly SlotInfo[]): void {
  const s = storage();
  if (s === null) return;
  const sorted = [...rows].sort((a, b) => (a.slot < b.slot ? -1 : a.slot > b.slot ? 1 : 0));
  try {
    s.setItem(INDEX_KEY, JSON.stringify(sorted));
  } catch {
    // An index that will not fit is not worth failing a save over.
  }
}

export function listSaves(): readonly SlotInfo[] {
  return [...readIndex()].sort((a, b) => b.savedAt - a.savedAt);
}

export function hasSave(slot = AUTOSAVE_SLOT): boolean {
  const s = storage();
  if (s === null) return false;
  try {
    return s.getItem(PREFIX + slot) !== null;
  } catch {
    return false;
  }
}

/**
 * Write one slot.
 *
 * A full 144x144 two-level game is about 1.4 MiB of canonical JSON, and a
 * browser counts local storage in UTF-16 code units, so it charges roughly 2.9
 * MB against a 5 MB origin quota. One slot fits and three do not, which is why
 * a quota failure is *reported* rather than swallowed: silently keeping the
 * previous save under the same name is the worst possible answer.
 */
export function saveToSlot(
  state: GameState, slot = AUTOSAVE_SLOT, options?: GameOptions,
): SaveOutcome {
  const s = storage();
  if (s === null) return { ok: false, message: "This browser is not letting the page store data." };

  const text = saveGame(state);
  const info: SlotInfo = {
    slot,
    day: state.turn.day,
    week: state.turn.week,
    month: state.turn.month,
    savedAt: Date.now(),
    bytes: text.length,
    ...(options === undefined ? {} : { options }),
  };
  try {
    s.setItem(PREFIX + slot, text);
  } catch (e: unknown) {
    const quota = e instanceof Error && /quota|exceed/i.test(e.message);
    return {
      ok: false,
      message: quota
        ? `This game is ${(text.length / 1048576).toFixed(1)} MiB and does not fit in `
          + "browser storage. Save it to a file instead."
        : `Could not write the save: ${e instanceof Error ? e.message : String(e)}`,
    };
  }
  writeIndex([...readIndex().filter((r) => r.slot !== slot), info]);
  return { ok: true, info };
}

export function deleteSlot(slot: string): void {
  const s = storage();
  if (s === null) return;
  try {
    s.removeItem(PREFIX + slot);
  } catch { /* nothing to delete */ }
  writeIndex(readIndex().filter((r) => r.slot !== slot));
}

const explain = (e: PersistError): string => persistErrorText(e);

/** The index row for one slot, which is where a slot's setup is kept. */
export function slotInfo(slot: string): SlotInfo | null {
  return readIndex().find((r) => r.slot === slot) ?? null;
}

export function loadFromSlot(slot = AUTOSAVE_SLOT, content?: ContentRegistry): LoadOutcome {
  const s = storage();
  if (s === null) return { ok: false, message: "This browser is not letting the page read stored data." };
  let text: string | null = null;
  try {
    text = s.getItem(PREFIX + slot);
  } catch {
    return { ok: false, message: "Could not read browser storage." };
  }
  if (text === null) return { ok: false, message: `There is no save in "${slot}".` };
  const r = loadFromText(text, content);
  if (!r.ok) return r;
  const row = slotInfo(slot);
  return row?.options === undefined ? r : { ...r, options: row.options };
}

/** The one read path. A file, a slot and a drag-and-drop all end up here. */
export function loadFromText(text: string, content?: ContentRegistry): LoadOutcome {
  const r = content === undefined ? parseSave(text) : parseSave(text, { content });
  if (!r.ok) return { ok: false, message: explain(r.error) };
  return { ok: true, state: r.value.save.state, migrated: r.value.migrated };
}

// --------------------------------------------------------------------- file

/** A stable, sortable filename. The day is in it because that is what a player
 *  looks for in a folder of saves. */
export function saveFileName(state: GameState): string {
  const day = String(state.turn.day).padStart(3, "0");
  return `realm-seed${String(state.rootSeed)}-day${day}.realm.json`;
}

/**
 * Hand the save to the browser as a download.
 *
 * An object URL rather than a data URI: a data URI of 1.4 MiB is a 1.9 MiB
 * string that some browsers refuse outright, and the blob is revoked on the
 * next frame so the copy does not sit in memory for the rest of the session.
 */
export function downloadSave(state: GameState, filename = saveFileName(state)): void {
  const blob = new Blob([saveGame(state)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => { URL.revokeObjectURL(url); }, 0);
}

/** Read a file the player picked. Returns the same outcome shape as a slot. */
export async function loadFromFile(file: Blob, content?: ContentRegistry): Promise<LoadOutcome> {
  try {
    return loadFromText(await file.text(), content);
  } catch (e: unknown) {
    return { ok: false, message: `Could not read the file: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/** The state a save would contain, as text, for a bug report attachment. */
export const saveText = (state: GameState): string => saveGame(state);

/** Round-trip a state through the format without touching storage. Used by the
 *  debug surface: if this ever disagrees, the save on disk is already wrong. */
export function saveRoundTripsCleanly(state: GameState): boolean {
  const back = loadGame(saveGame(state));
  return back.ok && saveGame(back.value) === saveGame(state);
}

// -------------------------------------------------------------- preferences

/**
 * The setup the player last started or resumed a game on.
 *
 * Two things read it. The New Game screen opens on it, so a player who plays
 * Impossible does not set it again every time; and a save loaded from a *file*
 * resumes on it, because a file carries no index row and the alternative -
 * falling back to the shipped defaults - silently hands an Impossible player a
 * Normal opponent, which is the defect this exists to close.
 */
export function rememberOptions(options: GameOptions): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(OPTIONS_KEY, JSON.stringify(options));
  } catch { /* a preference that will not persist is not worth failing over */ }
}

export function rememberedOptions(): Partial<GameOptions> | null {
  const s = storage();
  if (s === null) return null;
  try {
    const raw = s.getItem(OPTIONS_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    return parsed as Partial<GameOptions>;
  } catch {
    return null;
  }
}
